import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { Integrations } from "@/pages/admin/AdminDashboard";
import PushSettingsCard from "@/components/admin/PushSettingsCard";
import AutoReplySettingsCard from "@/components/admin/AutoReplySettingsCard";
import MetaLeadsStatusCard from "@/components/admin/MetaLeadsStatusCard";
import { useAuth } from "@/lib/auth";

/**
 * /settings — Minimal settings page.
 * Section 1: Integraciones (reuses existing Integrations component).
 * Section 2: Respuesta automática (Auto Reply SHADOW / SEND). Aquí y no en
 *   /automations: /settings está en el menú móvil, y apagar el envío tiene
 *   que poder hacerse desde el teléfono.
 * Section 3: Notificaciones push.
 * Section 4: Preferencias básicas (read-only info about the current account).
 */
export default function SettingsPage() {
  const { user } = useAuth();
  const { hash } = useLocation();

  // El Centro de acción enlaza a /settings#meta-lead-ads: se baja hasta la
  // tarjeta (otra vez al poco, cuando ya ha cargado y la página ha crecido).
  useEffect(() => {
    if (!hash) return undefined;
    const ir = () => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
    ir();
    const t = setTimeout(ir, 600);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <AdminShell>
      <div className="mb-8 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Ajustes</div>
        <h1 className="font-serif-dv text-4xl">Configuración de la plataforma.</h1>
        <p className="text-stone-500 mt-2">Integraciones externas y preferencias básicas.</p>
      </div>

      <Integrations />

      <Section
        title="Respuesta automática (WhatsApp)"
        subtitle="Procesamiento en sombra y envío real. Se cambia al momento, sin reiniciar nada."
      >
        <AutoReplySettingsCard />
      </Section>

      <div id="meta-lead-ads" className="scroll-mt-20">
        <Section
          title="Meta Lead Ads"
          subtitle="Formularios de anuncios de Meta, directos al CRM. Los interruptores están en el servidor."
        >
          <MetaLeadsStatusCard />
        </Section>
      </div>

      <Section
        title="Notificaciones"
        subtitle="Push en este dispositivo (requiere permiso del navegador; en iPhone, añadir Dolce a la pantalla de inicio)."
      >
        <PushSettingsCard />
      </Section>

      <Section title="Preferencias" subtitle="Información de tu cuenta activa.">
        <div className="dv-card grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Usuario</div>
            <div className="font-medium" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-xs text-stone-500">{user?.email}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Rol</div>
            <div className="font-medium capitalize" data-testid="settings-user-role">{user?.role}</div>
            <div className="text-xs text-stone-500">Proveedor: {user?.provider || "email"}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Plataforma</div>
            <div className="font-medium">Dolce Villas</div>
            <div className="text-xs text-stone-500">Versión interna · {new Date().getFullYear()}</div>
          </div>
        </div>
      </Section>
    </AdminShell>
  );
}
